import { OfferControls, OfferFormGroup } from './offer-form-group.model';
import { Offer } from './offer.model';
import { BaryValidator } from '../validators/bary.validator';
import { IFormBuilder } from './forms/IFormBuilder';
import { BaryFormFactoryService } from '../services/bary-form-factory.service';
import { OfferViewDTO } from './offer-view-dto.type';
import { PricingUnitEnum } from './enums/pricing-unit.enum';
import { LIST_PRICING_UNIT } from './measure-unit.model';
import { FormControl, Validators } from '@angular/forms';
import { OfferMerchandiseTypeEnum } from './enums/offer-merchandise-type.enum';

interface IOfferFormGroupBuilder extends IFormBuilder<OfferControls> {
  init(offer?: Offer): IOfferFormGroupBuilder;

  build(): OfferFormGroup;
}

export class OfferFormGroupBuilder implements IOfferFormGroupBuilder {
  private formName = 'offerFormGroup';
  private controls!: OfferControls;

  constructor(private bcf: BaryFormFactoryService) {}

  public init(offer: OfferViewDTO): OfferFormGroupBuilder {
    this.controls = this.bcf.makeOfferControls(offer);
    this.initListeners();
    return this;
  }

  public build(): OfferFormGroup {
    const form = new OfferFormGroup(this.formName, this.controls, [
      BaryValidator.timeslotRangeValidator(this.controls.pickupTimeslotStart, this.controls.pickupTimeslotEnd, this.controls.pickupTimeslotRange, 'invalidRangePickup'),
      BaryValidator.timeslotRangeValidator(this.controls.deliveryTimeslotStart, this.controls.deliveryTimeslotEnd, this.controls.deliveryTimeslotRange, 'invalidRangeDelivery'),
      BaryValidator.hasAtLeastOneInsuranceItem(this.controls.insuranceItems, this.controls.status),
    ]);

    if (this.controls.isControlledTemperature?.value) {
      form.getAngularForm().addValidators(BaryValidator.hasMinValueLowerThanMaxValue(this.controls.minTemperatureThreshold!, this.controls.maxTemperatureThreshold!, 10, '°C'));
    }

    if (this.controls.merchandiseType.value === OfferMerchandiseTypeEnum.UNCONDITIONED) {
      form.getAngularForm().addValidators(BaryValidator.hasAtLeastOneVehicle(this.controls.offerVehicles, this.controls.status));
    }

    return form;
  }

  private initListeners(): void {
    this.controls.measureUnit.valueChanges.subscribe({
      next: (val: PricingUnitEnum): void => {
        if ([PricingUnitEnum.PALLET, PricingUnitEnum.KILOGRAM, PricingUnitEnum.LDM].includes(val)) {
          this.controls.toWeightWeighted.setValue(true);
          this.controls.weightingFactor.setValue(LIST_PRICING_UNIT[val].defaultValue);
          this.controls.weightUnit = new FormControl({ value: LIST_PRICING_UNIT[val]!.measureUnit?.value, disabled: true });
        } else {
          this.controls.toWeightWeighted.setValue(undefined);
          this.controls.weightingFactor.setValue(undefined);
        }
      },
    });

    this.controls.toWeightWeighted.valueChanges.subscribe({
      next: (val: boolean): void => {
        if (val) {
          this.controls.weightingFactor.enable();
          this.controls.weightingFactor.addValidators([Validators.required, Validators.min(0)]);
        } else {
          this.controls.weightingFactor.removeValidators([Validators.required, Validators.min(0)]);
          this.controls.weightingFactor.setValue(null);
          this.controls.weightingFactor.disable();
        }
      },
    });

    this.controls.offerVehicles?.valueChanges.subscribe({
      next: (val: any[]): void => {
        if (val.length === 0) {
          this.controls.status.setValue(false);
          this.controls.status.updateValueAndValidity();
        }
      },
    });

    this.controls.insuranceItems?.valueChanges.subscribe({
      next: (val: any[]): void => {
        if (val.length === 0) {
          this.controls.status.setValue(false);
          this.controls.status.updateValueAndValidity();
        }
      },
    });
  }
}
